import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { api, ApiError } from '../lib/api.ts';
import { writeBuyerSession } from '../lib/buyer-session.ts';
import { nextStopForBuyer } from '../lib/buyer-flow.ts';

// Validate the magic-link query string up front so the route component
// gets a typed token. TanStack Router runs validateSearch on every nav.
const ConsumeSearch = z.object({ t: z.string().min(8).max(512) });

// Magic-link landing handler. Reads ?t=<token>, calls
// POST /v1/public/sessions/consume to exchange it for a session cookie,
// then asks the API for invoice + KYC status, then routes the buyer to
// /kyc | /on-ramp | /tracking via the pure decision helper.
function ConsumePage() {
  const { publicId } = Route.useParams();
  const { t: token } = Route.useSearch();
  const navigate = useNavigate();
  const intl = useIntl();
  const [status, setStatus] = useState<'pending' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Single-use token + React StrictMode's double effect-invoke (dev) would
  // consume the token twice and strand the buyer with "already used" — see the
  // matching guard in the dashboard auth.consume route. Run the exchange once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    async function consume(): Promise<void> {
      try {
        const session = await api.consumeMagicLink(token);
        writeBuyerSession({ buyerId: session.buyerId, publicId });

        // Resolve invoice + KYC in parallel. KYC may legitimately 404
        // for a brand new buyer; treat that as "no submission yet".
        const [invoice, kycResult] = await Promise.all([
          api.getPublicInvoice(publicId).catch((e: unknown) => {
            // Re-throw so the outer catch surfaces a generic consume error.
            throw e;
          }),
          // Opt out of the global 401 kick-out so a cookie-blocked browser
          // surfaces the consume error here instead of bounce-looping to
          // signin?reason=expired.
          api.getBuyerKyc(session.buyerId, { skipAuthRedirect: true }).catch((e: unknown) => {
            if (e instanceof ApiError && e.status === 404) return null;
            throw e;
          }),
        ]);

        const next = nextStopForBuyer({
          invoiceStatus: invoice.status,
          kycStatus: kycResult ? kycResult.status : null,
        });

        const target =
          next === 'kyc'
            ? '/r/$publicId/kyc'
            : next === 'on-ramp'
              ? '/r/$publicId/on-ramp'
              : '/r/$publicId/tracking';

        await navigate({
          to: target,
          params: { publicId },
          replace: true,
        });
      } catch (err) {
        setStatus('error');
        if (err instanceof ApiError) {
          if (err.code === 'InvalidMagicLink') {
            setErrorMessage(intl.formatMessage({ id: 'consume.error.invalid' }));
            return;
          }
          if (err.code === 'MagicLinkAlreadyUsed') {
            setErrorMessage(intl.formatMessage({ id: 'consume.error.used' }));
            return;
          }
          if (err.code === 'MagicLinkExpired') {
            setErrorMessage(intl.formatMessage({ id: 'consume.error.expired' }));
            return;
          }
        }
        setErrorMessage(intl.formatMessage({ id: 'consume.error.generic' }));
      }
    }
    void consume();
  }, [token, publicId, navigate, intl]);

  return (
    <div className="mx-auto max-w-md px-4 py-12 text-center">
      {status === 'pending' && (
        <p className="text-[var(--color-navy-600)]">
          <FormattedMessage id="consume.loading" />
        </p>
      )}
      {status === 'error' && (
        <div>
          <p className="text-[var(--color-danger)]" role="alert">
            {errorMessage}
          </p>
          <Link
            to="/r/$publicId/signin"
            params={{ publicId }}
            className="mt-4 inline-block rounded-md border border-[var(--color-navy-200)] px-3 py-1.5 text-sm text-[var(--color-navy-800)] hover:bg-[var(--color-navy-50)]"
          >
            <FormattedMessage id="consume.cta.requestNew" />
          </Link>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/r/$publicId/consume')({
  validateSearch: ConsumeSearch,
  component: ConsumePage,
});
